import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  CheckCircle,
  Cancel,
  VpnKey,
  Warning,
} from '@mui/icons-material';
import axios from 'axios';

const ForceResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [isClientOwner, setIsClientOwner] = useState(false);
  const [ownerType, setOwnerType] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    // Get userId from location state (passed from login)
    if (location.state?.userId) {
      setUserId(location.state.userId);
      setUserEmail(location.state.email || '');
      setUserName(location.state.name || '');
      setIsClientOwner(location.state.isClientOwner === true || location.state.isClientOwner === 'true');
      setOwnerType(location.state.ownerType || null);
      setResetToken(location.state.resetToken || null);
      console.log('Client Owner flag:', location.state.isClientOwner);
      console.log('Owner type:', location.state.ownerType);
    } else {
      // If no userId, redirect to login
      console.error('ERROR: No userId provided in location state, redirecting to login');
      console.error('Location state was:', location.state);
      navigate('/login', { replace: true });
    }
  }, [location.state, navigate]);

  // Check password strength
  useEffect(() => {
    setPasswordStrength({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[@$!%*?&]/.test(newPassword),
    });
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Check if all strength requirements are met
    const allRequirementsMet = Object.values(passwordStrength).every(val => val);
    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }

    if (!userId) {
      setError('User ID is missing. Please try logging in again.');
      return;
    }

    if (!resetToken) {
      setError('Reset token is missing. Please try logging in again.');
      return;
    }

    setLoading(true);

    try {
      const requestPayload = {
        userId,
        newPassword,
        resetToken,
      };

      // Include Client Owner flags if this is a Client Owner
      if (isClientOwner) {
        requestPayload.isClientOwner = true;
        if (ownerType) {
          requestPayload.ownerType = ownerType;
        }
      }

      const response = await axios.post('/api/auth/force-reset-password', requestPayload);

      if (response.data.success) {
        setSuccess(true);

        // Clear any existing tokens
        localStorage.removeItem('token');

        // Show success message and redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successfully! Please log in with your new password.',
              email: userEmail
            }
          });
        }, 3000);
      }
    } catch (err) {
      console.error('Force reset password error:', err);
      setError(
        err.response?.data?.message ||
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const strengthIndicator = (met) => (
    <ListItemIcon>
      {met ? (
        <CheckCircle sx={{ color: 'success.main' }} />
      ) : (
        <Cancel sx={{ color: 'error.main' }} />
      )}
    </ListItemIcon>
  );

  if (!userId) {
    return null; // Will redirect in useEffect
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Warning sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You are using a temporary password. Please set a new permanent password to continue.
            </Typography>
            {userName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Hello, {userName}
              </Typography>
            )}
          </Box>

          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="medium">
                Password reset successfully! You will be logged out and redirected to the login page.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please log in again with your new password.
              </Typography>
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          {!success && (
            <Box component="form" onSubmit={handleSubmit}>
              {/* New Password */}
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm New Password */}
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={confirmPassword && newPassword !== confirmPassword}
                helperText={
                  confirmPassword && newPassword !== confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Requirements */}
              {newPassword && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                    Password Requirements:
                  </Typography>
                  <List dense>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.length)}
                      <ListItemText
                        primary="At least 8 characters"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.length ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.uppercase)}
                      <ListItemText
                        primary="One uppercase letter (A-Z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.uppercase ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.lowercase)}
                      <ListItemText
                        primary="One lowercase letter (a-z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.lowercase ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.number)}
                      <ListItemText
                        primary="One number (0-9)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.number ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.special)}
                      <ListItemText
                        primary="One special character (@$!%*?&)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.special ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              {/* Loading Progress */}
              {loading && <LinearProgress sx={{ mt: 2 }} />}
            </Box>
          )}

          {/* Security Note */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              🔒 Your password is encrypted and secure. After resetting, you will be logged out and must log in again.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForceResetPassword;
